import React, { ReactElement, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { appActions } from '../../state/modules/app';
import { makeStyles, Toaster, ToastTrigger, useToastController } from '@fluentui/react-components';
import {
  AlertPosition,
  AlertType,
  ICompanyUser,
  ICompanyUserInput,
  ICompanyUserNotificationUpdateMutationResult,
  ICompanyUserNotificationUpdateMutationVariables,
  Maybe,
  NotificationType,
} from '../../graphql/schema/p4types';
import Notification, { NotificationContainer } from '../../components/Notification';
import { BlockNotification } from '../../components/Notification/BlockNotification';
import { ToastIntent } from '@fluentui/react-toast';
import AppContext from '../../components/Context/AppContext';
import UserContext from '../../components/Context/UserContext';
import { useMutation } from '@apollo/client';
import CompanyUserNotificationUpdate from '../../graphql/mutations/company/CompanyUserNotificationMutation.graphql';
import getClientMutationId from '../../utils/getClientMutationId';
import { IJobMeta } from '../../utils/BackgroundJobs';
import generateClientMutationId from '../../graphql/schema/generateClientMutationId';
import { JobNotification } from '../../components/Notification/JobNotification';
import { BasicHeader } from '../../actions/BasicHeader';
import { ModalBottomButtonContainer } from '../../components/modals/Styles';
import { SecondaryButton } from '../../components/connectedForm/Button';
import {
  notificationBackgroundColours,
  notificationBorderColours,
  NotificationIcon,
  setNotificationIcon,
} from '../../components/Notification/HeaderNotification';
import FluentModal from '../../components/modals/FluentModal';

const useStyles = makeStyles({
  toaster: {
    width: '500px',
  },
});

/**
 * Convenience wrapper for notifications dispatched in the older manner (I.e. We have a 'notification' object from the redux store to convert).
 */
const Toastification = ({ notification }: { notification: any }): ReactElement => {
  if (notification.title && (!notification.validation || notification.validation.length === 0 || notification.form)) {
    return (
      <Notification
        title={notification.title ? notification.title : notification.type}
        message={notification.general}
        type={notification.type}
        key={notification.alertId}
        errorId={notification.e}
      />
    );
  }
  throw new Error('Notification is misconfigured and cannot be displayed.');
};

/**
 * Controller for generating P4 toasts & adding them to the notification history appropriately (On generation).
 * dispatchDeprecatedToast - Dispatch a toast using a notification object per our old 'redux-based' notification system.
 * dispatchJobToast - Dispatch a toast using a job as the driving object.
 * dispatchToast - Dispatch a regular or custom toast (Direct pass-through of the regular toast controller version).
 */
export const useP4ToastController = (onBlockingNotificationDismissed?: () => void) => {
  const { toasterId } = useContext(AppContext);
  const { currentUser } = useContext(UserContext);
  const { dispatchToast, dismissToast, ...rest } = useToastController(toasterId);
  const dispatch = useDispatch();

  const [notificationUpdate] = useMutation<
    ICompanyUserNotificationUpdateMutationResult,
    ICompanyUserNotificationUpdateMutationVariables
  >(CompanyUserNotificationUpdate, {
    errorPolicy: 'ignore',
    update: (dataProxy, { data }) => {
      if (!data?.companyUserNotificationUpdate?.result) return;

      dataProxy.modify({
        fields: {
          companyUserNotifications: (existingData, { DELETE }) => {
            //Wipe out the cache - Doing a direct update seems to not function as expected in Apollo v3+ - We just need to ensure the data is fresh if
            //                     we open "recent notifications" so this should suffice.
            return DELETE;
          },
        },
      });
    },
  });

  //Creates a fluent-ui toast notification & updates the P4 notification history accordingly.
  const dispatchDeprecatedToast = useCallback(
    (notification: any, options?: Parameters<typeof dispatchToast>[1]): void => {
      //Avoid loop - If we hit a timeout, then a notification gets dispatched, but then this notification history update also times out...repeat.
      if (
        notification.title !== 'Request timed out' &&
        notification.title !== 'You do not have sufficient permission to access this resource'
      ) {
        if (currentUser.companyUser) {
          notificationUpdate({
            variables: {
              input: {
                clientMutationId: getClientMutationId(),
                companyUserNotification: {
                  title: notification.title ?? 'Untitled',
                  message: notification.general ?? 'No message provided',
                  alertType: notification.type as AlertType,
                  companyUser: { id: currentUser.companyUser.id } as ICompanyUserInput,
                },
              },
            },
          });
        }
      }

      const toastId = options?.toastId ?? notification.alertId ?? generateClientMutationId();
      const standardOptions = { ...options, toastId: toastId };

      dispatchToast(<Toastification notification={notification} />, standardOptions);
    },
    [currentUser.companyUser?.id, dispatchToast, notificationUpdate]
  );

  //Creates a fluent-ui toast notification & updates the P4 notification history accordingly.
  const dispatchJobToast = useCallback(
    (job: IJobMeta, options?: Parameters<typeof dispatchToast>[1]): void => {
      if (currentUser.companyUser) {
        notificationUpdate({
          variables: {
            input: {
              clientMutationId: getClientMutationId(),
              companyUserNotification: {
                title: job.title,
                message: job.message,
                alertType: AlertType.Info as AlertType,
                companyUser: { id: currentUser.companyUser.id } as ICompanyUserInput,
              },
            },
          },
        });
      }

      const toastId = options?.toastId ?? job.jobId ?? generateClientMutationId();
      const jobOptions: Parameters<typeof dispatchToast>[1] = { ...options, toastId: toastId, timeout: -1 };

      const dismiss = () => {
        dismissToast(toastId);
        dispatch(appActions.backgroundJob(job));
      };

      dispatchToast(
        <JobNotification title={job.title!} message={job.message!} type={AlertType.Info} job={job} dismiss={dismiss} />,
        jobOptions
      );
    },
    [currentUser.companyUser?.id, dispatch, dispatchToast, notificationUpdate, dismissToast]
  );

  /**
   * @param errorId - Sentry error id, for feedback link (Not expected to be provided for normal calls to this function).
   */
  const dispatchSimpleToast = useCallback(
    (title: string, message: string, type: AlertType, { errorId }: { errorId?: string } = {}) => {
      const { intent, notificationType } = parseType(currentUser.companyUser, type);

      // Always create a promise, but conditionally include the mutation
      const notificationPromise = currentUser.companyUser
        ? notificationUpdate({
            variables: {
              input: {
                clientMutationId: getClientMutationId(),
                companyUserNotification: {
                  title: title,
                  message: message,
                  alertType: type,
                  companyUser: { id: currentUser.companyUser.id } as ICompanyUserInput,
                },
              },
            },
          })
        : Promise.resolve(); // Return a resolved promise if no companyUser

      notificationPromise.finally(() => {
        if (notificationType === NotificationType.Off) {
          return;
        }
        dispatchToast(<Notification title={title} message={message} type={type} errorId={errorId} />, {
          intent,
          timeout: notificationType === NotificationType.Persistent ? -1 : 3000,
        });
      });
    },
    [currentUser.companyUser, dispatchToast, notificationUpdate]
  );

  /**
   * Add a Toast into notification history that is not displayed in the usual bottom-right toast area.
   * To be used sparingly - Only use as-of-writing is for automated concurrency merges.
   */
  const dispatchSilentToast = useCallback(
    (title: string, message: string, type: AlertType) => {
      if (currentUser.companyUser) {
        notificationUpdate({
          variables: {
            input: {
              clientMutationId: getClientMutationId(),
              companyUserNotification: {
                title: title,
                message: message,
                alertType: type,
                companyUser: { id: currentUser.companyUser.id } as ICompanyUserInput,
              },
            },
          },
        });
      }
    },
    [currentUser.companyUser, notificationUpdate]
  );

  const dispatchBlockingToast = useCallback(
    (
      title: string,
      message: string,
      type: AlertType,
      options?: Omit<Parameters<typeof dispatchToast>[1], 'intent'>
    ) => {
      const { intent, notificationType } = parseType(currentUser.companyUser, type);

      const notificationPromise = currentUser.companyUser
        ? notificationUpdate({
            variables: {
              input: {
                clientMutationId: getClientMutationId(),
                companyUserNotification: {
                  title: title,
                  message: message,
                  alertType: type,
                  companyUser: { id: currentUser.companyUser.id } as ICompanyUserInput,
                },
              },
            },
          })
        : Promise.resolve();

      notificationPromise.finally(() => {
        const blockOptions: Parameters<typeof dispatchToast>[1] = { ...options, intent, timeout: -1 };
        const iconStyle = setNotificationIcon(type);
        dispatchToast(
          <FluentModal
            isOpen={true}
            styles={{
              main: {
                border: 'solid 1px',
                borderColor: notificationBorderColours[type as keyof typeof notificationBorderColours],
              },
            }}
          >
            <BasicHeader
              styles={{
                container: {
                  backgroundColor: notificationBackgroundColours[type as keyof typeof notificationBorderColours],
                  border: 'solid 1px',
                  borderColor: notificationBorderColours[type as keyof typeof notificationBorderColours],
                },
              }}
            >
              <NotificationIcon type={type} style={{ paddingRight: '8px' }} fontSize={'22px'}>
                {iconStyle}
              </NotificationIcon>
              {title}
            </BasicHeader>
            <div style={{ padding: '1em' }}>{message}</div>
            <ModalBottomButtonContainer>
              <ToastTrigger>
                <SecondaryButton onClick={() => onBlockingNotificationDismissed?.()}>Dismiss</SecondaryButton>
              </ToastTrigger>
            </ModalBottomButtonContainer>
          </FluentModal>,
          blockOptions
        );
      });
    },
    [currentUser.companyUser, dispatchToast, notificationUpdate, onBlockingNotificationDismissed]
  );

  /**
   * Wrapper around toaster dispatch to add the history update to the process.
   */
  const dispatchToastWithNotificationHistory = useCallback(
    (
      content: Parameters<typeof dispatchToast>[0],
      details: { title: string; message?: string; type: AlertType } & Parameters<typeof dispatchToast>[1]
    ) => {
      const { title, message, type, ...toastOptions } = details;
      const { intent, notificationType } = parseType(currentUser.companyUser, type);

      const notificationPromise = currentUser.companyUser
        ? notificationUpdate({
            variables: {
              input: {
                clientMutationId: getClientMutationId(),
                companyUserNotification: {
                  title: title,
                  message: message ?? title,
                  alertType: type,
                  companyUser: { id: currentUser.companyUser.id } as ICompanyUserInput,
                },
              },
            },
          })
        : Promise.resolve();

      notificationPromise.finally(() => {
        if (notificationType === NotificationType.Off) {
          return;
        }
        dispatchToast(content, {
          intent,
          timeout: notificationType === NotificationType.Persistent ? -1 : 3000,
          ...toastOptions,
        });
      });
    },
    [currentUser.companyUser, dispatchToast, notificationUpdate]
  );

  return {
    dispatchDeprecatedToast: dispatchDeprecatedToast,
    dispatchJobToast: dispatchJobToast,
    dispatchSimpleToast: dispatchSimpleToast,
    dispatchBlockingToast: dispatchBlockingToast,
    dispatchToast: dispatchToastWithNotificationHistory,
    dispatchSilentToast: dispatchSilentToast,
    dismissToast,
    ...rest,
  };
};

const parseType = (companyUser: Maybe<ICompanyUser>, alertType: AlertType) => {
  let notificationType: NotificationType;
  let intent: ToastIntent | undefined;
  switch (alertType) {
    case AlertType.Info:
    case AlertType.IndeterminateProgress:
      notificationType = companyUser?.infoNotifications ?? NotificationType.Persistent;
      intent = 'info';
      break;
    case AlertType.Success:
      notificationType = companyUser?.successNotifications ?? NotificationType.Persistent;
      intent = 'success';
      break;
    case AlertType.Warning:
      notificationType = companyUser?.warningNotifications ?? NotificationType.Persistent;
      intent = 'warning';
      break;
    case AlertType.Error:
      notificationType = companyUser?.errorNotifications ?? NotificationType.Persistent;
      intent = 'error';
      break;
    case AlertType.Action:
      notificationType = NotificationType.Persistent;
      intent = 'info';
      break;
    default:
      notificationType = NotificationType.Persistent;
      break;
  }

  return { intent, notificationType };
};

export const NotificationsArea = ({ currentUser, toasterId }: { currentUser: any; toasterId: string }) => {
  const dispatch = useDispatch();
  const alert = useSelector((store: any) => store.data.alert);
  const jobMap = useSelector((store: any) => store.app.jobMap);
  const toasterStyles = useStyles();

  const { dispatchDeprecatedToast, dispatchJobToast } = useP4ToastController();

  const firedNotifications = useRef<{ [alertId: string]: boolean }>({});
  const [blockNotifications, setBlockNotifications] = useState<{ [alertId: string]: ReactElement }>({});

  /**
   * This effect is effectively a forwarding component for older notification types, to generate new P4 Toast Notifications.
   * Newly added toasts are expected to be dispatched using the P4 Toast Controller directly.
   */
  useEffect(() => {
    const { companyUser } = currentUser;
    if (!(alert.notifications && companyUser)) {
      return;
    }
    Object.keys(alert.notifications).forEach((id) => {
      const notification = alert.notifications[id];
      if (firedNotifications.current[notification.alertId]) {
        //Already processed - TODO - Should consider removing from the store directly at this point
        return;
      }
      firedNotifications.current[notification.alertId] = true;

      const { intent, notificationType } = parseType(companyUser, notification.type);

      //If the notification type is disabled, simply do not generate it.
      if (notificationType === NotificationType.Off) {
        return;
      }

      //If we're presenting a block type notification, drop it into the blockNotifications state to render instead of using the Toaster system.
      if (notification.alert?.alertPosition === AlertPosition.Block) {
        setBlockNotifications((prevState) => ({
          ...prevState,
          [notification.alertId]: (
            <BlockNotification
              key={notification.alertId}
              alertId={notification.alertId}
              alert={notification.alert}
              dismiss={() =>
                setBlockNotifications((prevState1) => {
                  const newState = { ...prevState1 };
                  delete newState[notification.alertId];
                  return newState;
                })
              }
            />
          ),
        }));
      }

      dispatchDeprecatedToast(notification, {
        toastId: notification.alertId,
        timeout: notificationType === NotificationType.Persistent ? -1 : 3000,
        intent: intent,
      });
    });
  }, [alert.notifications, currentUser, dispatch, dispatchDeprecatedToast]);

  useEffect(() => {
    if (!jobMap) return;
    Object.keys(jobMap)
      .filter((jobId) => {
        const job = jobMap[jobId];
        return !job.backgrounded && !job.complete && !job.failed;
      })
      .forEach((jobId) => {
        const job = jobMap[jobId];
        if (!firedNotifications.current[jobId] && job.updateRequired) {
          dispatchJobToast(job, { toastId: jobId });
          firedNotifications.current[jobId] = true;
        }
      });
  }, [dispatchJobToast, jobMap]);

  return (
    <>
      <NotificationContainer>{Object.values(blockNotifications)}</NotificationContainer>
      <Toaster toasterId={toasterId} className={toasterStyles.toaster} />
    </>
  );
};
